export type LinkType = 'BACK' | 'PAGE' | 'ELLIPSIS' | 'FORWARD';

type LinkObject = {
    type: LinkType;
    key: string | number;
    pageNumber?: number;
    isSelected?: boolean;
};

type GetLinksProps = {
    numberOfPages: number;
    currentPage: number;
    totalPageLinks?: number;
    showNumbers?: boolean;
};

const TOTAL_PAGE_LINKS_DEFAULT = 6;

// inclusive range
function range(start: number, end: number): number[] {
    const arr = [];
    for (let i = start; i <= end; i = i + 1) {
        arr.push(i);
    }
    return arr;
}

/**
 * Returns positions of back, forward, page numbers, and ellipsis for page links
 * @param numberOfPages
 * @param currentPage
 */
export const getLinks = function ({
    numberOfPages,
    currentPage,
    totalPageLinks,
    showNumbers = true,
}: GetLinksProps): LinkObject[] {
    let currentLinks: LinkObject[] = [];
    totalPageLinks = totalPageLinks || TOTAL_PAGE_LINKS_DEFAULT;

    // calculate the number of links with numbers (as opposed to the "< Prev" and "Next >" links)
    const hasPrevious = currentPage > 1;
    const hasNext = currentPage < numberOfPages;

    let countRelativeLinks = 0;

    if (hasPrevious) {
        countRelativeLinks++;
    }
    if (hasNext) {
        countRelativeLinks++;
    }

    const countNumberedLinks = totalPageLinks - countRelativeLinks;

    function getPage(i: number): LinkObject {
        return {
            type: 'PAGE',
            key: i,
            pageNumber: i,
            isSelected: i === currentPage,
        };
    }

    if (hasPrevious) {
        currentLinks.push({
            type: 'BACK',
            key: 'back',
        });
    }

    if (showNumbers) {
        const halfNumLinks = countNumberedLinks / 2;

        if (numberOfPages <= countNumberedLinks) {
            // If there are fewer than the total pages, only display links for those pages every time.
            if (numberOfPages > 1) {
                currentLinks = currentLinks.concat(range(1, numberOfPages).map(getPage));
            }
        } else if (currentPage <= halfNumLinks) {
            // If the current page is in the beginning section
            // For 6 links: (Previous, 1, 2, 3, ..., 100, Next)
            // For 12 link: (Previous, 1, 2, 3, 4, 5, 6, 7, 8, 9, ..., 100, Next)
            currentLinks = currentLinks.concat(range(1, countNumberedLinks - 1).map(getPage));
            currentLinks.push({ type: 'ELLIPSIS', key: 'end-ellipsis' });
            currentLinks.push(getPage(numberOfPages));
        } else if (currentPage > numberOfPages - halfNumLinks) {
            // If the current page is in the end section
            // For 6 links: (Previous, 1, ..., 98, 99, 100, Next)
            // For 12: (Previous, 1, ..., 92, 93, 94, 95, 96, 97, 98, 99, 100, Next)
            currentLinks.push(getPage(1));
            currentLinks.push({ type: 'ELLIPSIS', key: 'start-ellipsis' });
            currentLinks = currentLinks.concat(
                range(numberOfPages - countNumberedLinks + 2, numberOfPages).map(getPage)
            );
        } else {
            // If the current page is in the middle somewhere
            // For 6 links: (Previous, 1,..., 50, 51, ..., 100, Next)
            // For 12 links: (Previous, 1, ..., 47, 48, 49, 50, 51, 52, 53, 54, ..., 100, Next)
            currentLinks.push(getPage(1));
            currentLinks.push({ type: 'ELLIPSIS', key: 'start-ellipsis' });
            const middleOffset = Math.floor((countNumberedLinks - 4) / 2);

            // need to add one more middle link for odd amounts of links
            const isOdd = totalPageLinks % 2;

            currentLinks = currentLinks.concat(
                range(currentPage - middleOffset - isOdd, currentPage + middleOffset + 1).map(
                    getPage
                )
            );
            currentLinks.push({ type: 'ELLIPSIS', key: 'end-ellipsis' });
            currentLinks.push(getPage(numberOfPages));
        }
    }

    if (hasNext) {
        currentLinks.push({
            type: 'FORWARD',
            key: 'forward',
        });
    }

    return currentLinks;
};
